<template>
  <div class="two-factor-setup">
    <!-- Status atual do 2FA -->
    <div class="mb-4">
      <div v-if="initialLoading" class="flex items-center space-x-2 text-gray-600">
        <i class="pi pi-spin pi-spinner"></i>
        <span>Carregando status...</span>
      </div>
      
      <div v-else class="flex items-center justify-between">
        <div>
          <p class="text-sm text-gray-600">Status da Autenticação em Dois Fatores</p>
          <p class="font-medium" :class="is2FAEnabled ? 'text-green-600' : 'text-red-600'">
            {{ is2FAEnabled ? 'Ativada' : 'Desativada' }}
          </p>
        </div>
        <button
          v-if="!is2FAEnabled"
          @click="startSetup"
          class="bg-primary-dark text-white px-4 py-2 rounded hover:bg-primary-dark/90 transition-colors disabled:opacity-50"
          :disabled="loading"
        >
          <i v-if="loading" class="pi pi-spin pi-spinner mr-2"></i>
          Ativar 2FA
        </button>
        <button
          v-else
          @click="disable2FA"
          class="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 transition-colors disabled:opacity-50"
          :disabled="loading"
        >
          <i v-if="loading" class="pi pi-spin pi-spinner mr-2"></i>
          Desativar 2FA
        </button>
      </div>
    </div>

    <!-- Modal de configuração -->
    <Dialog 
      v-model:visible="showSetupModal" 
      :modal="true"
      header="Configurar Autenticação em Dois Fatores"
      :closable="true"
      :style="{ width: '450px' }"
      @hide="onHideModal"
    >
      <div class="space-y-4">
        <!-- Passo 1: QR Code -->
        <div v-if="setupStep === 1">
          <p class="mb-4">
            Escaneie o QR Code abaixo com seu aplicativo autenticador (Google Authenticator, Authy, etc.):
          </p>
          <div class="flex justify-center mb-4">
            <img :src="qrCodeUrl" alt="QR Code para 2FA" class="border p-2" />
          </div>
          <p class="text-sm text-gray-600 mb-4">
            Ou insira manualmente o código secreto:
            <code class="bg-gray-100 px-2 py-1 rounded">{{ secret }}</code>
          </p>
          <div class="flex justify-end">
            <button
              @click="setupStep = 2"
              class="bg-primary-dark text-white px-4 py-2 rounded hover:bg-primary-dark/90 transition-colors"
            >
              Próximo
            </button>
          </div>
        </div>

        <!-- Passo 2: Verificação -->
        <div v-if="setupStep === 2">
          <p class="mb-4">
            Digite o código de 6 dígitos gerado pelo seu aplicativo autenticador:
          </p>
          <div class="mb-4">
            <input
              v-model="verificationCode"
              type="text"
              pattern="[0-9]*"
              inputmode="numeric"
              maxlength="6"
              class="w-full px-3 py-2 border rounded-md"
              placeholder="000000"
              @input="handleInput"
            />
          </div>
          <div class="flex justify-end space-x-2">
            <button
              @click="setupStep = 1"
              class="bg-gray-100 text-gray-700 px-4 py-2 rounded hover:bg-gray-200 transition-colors"
            >
              Voltar
            </button>
            <button
              @click="verifyAndEnable"
              class="bg-primary-dark text-white px-4 py-2 rounded hover:bg-primary-dark/90 transition-colors disabled:opacity-50"
              :disabled="!verificationCode || verificationCode.length !== 6"
            >
              <i v-if="loading" class="pi pi-spin pi-spinner mr-2"></i>
              Verificar e Ativar
            </button>
          </div>
        </div>
      </div>
    </Dialog>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useToast } from 'primevue/usetoast'
import { useAuthStore } from '@/stores/auth'
import Dialog from 'primevue/dialog'
import { generateTOTP, verifyTOTP } from '@/utils/totp'
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore'
import { db } from '@/firebase'

const toast = useToast()
const authStore = useAuthStore()

const loading = ref(false)
const initialLoading = ref(true)
const is2FAEnabled = ref(false)
const showSetupModal = ref(false)
const setupStep = ref(1)
const secret = ref('')
const qrCodeUrl = ref('')
const verificationCode = ref('')

onMounted(async () => {
  initialLoading.value = true
  await check2FAStatus()
  initialLoading.value = false
})

function onHideModal() {
  console.log('Modal hidden')
  loading.value = false
  setupStep.value = 1
  secret.value = ''
  qrCodeUrl.value = ''
  verificationCode.value = ''
}

async function check2FAStatus() {
  try {
    const user = authStore.user
    if (!user?.uid) return

    const userDoc = await getDoc(doc(db, 'users', user.uid))
    const userData = userDoc.data()
    is2FAEnabled.value = userData?.twoFactorEnabled || false
  } catch (error) {
    console.error('Error checking 2FA status:', error)
    toast.add({
      severity: 'error',
      summary: 'Erro',
      detail: 'Não foi possível verificar o status do 2FA',
      life: 3000
    })
  }
}

async function startSetup() {
  console.log('startSetup called')
  try {
    loading.value = true
    console.log('Loading set to true')
    showSetupModal.value = true
    console.log('Modal visibility set to true')
    
    const user = authStore.user
    console.log('Current user:', user)
    
    if (!user?.email) {
      throw new Error('Usuário não encontrado')
    }

    console.log('Generating TOTP for:', user.email)
    const { secret: newSecret, qrCodeUrl: newQrCode } = await generateTOTP(user.email)
    console.log('TOTP generated successfully')
    
    secret.value = newSecret
    qrCodeUrl.value = newQrCode
    setupStep.value = 1
    
    console.log('Setup state:', {
      email: user.email,
      modalVisible: showSetupModal.value,
      step: setupStep.value,
      hasSecret: !!secret.value,
      hasQR: !!qrCodeUrl.value,
      loading: loading.value
    })
  } catch (error) {
    console.error('Error starting 2FA setup:', error)
    toast.add({
      severity: 'error',
      summary: 'Erro',
      detail: 'Não foi possível iniciar a configuração do 2FA',
      life: 3000
    })
  } finally {
    loading.value = false
  }
}

function handleInput(event: Event) {
  const input = event.target as HTMLInputElement
  const value = input.value.replace(/[^0-9]/g, '')
  verificationCode.value = value
}

async function verifyAndEnable() {
  try {
    // Validar o formato do código
    if (!/^\d{6}$/.test(verificationCode.value)) {
      toast.add({
        severity: 'error',
        summary: 'Erro',
        detail: 'O código deve conter exatamente 6 dígitos',
        life: 3000
      })
      return
    }

    loading.value = true
    const user = authStore.user
    console.log('Current user:', user)

    if (!user?.uid) {
      throw new Error('Usuário não autenticado')
    }

    console.log('Verificando código:', {
      secret: secret.value,
      code: verificationCode.value,
      timestamp: Math.floor(Date.now() / 1000)
    })

    const isValid = verifyTOTP(secret.value, verificationCode.value)
    if (!isValid) {
      toast.add({
        severity: 'error',
        summary: 'Erro',
        detail: 'Código inválido ou expirado. Por favor, tente novamente com um novo código.',
        life: 3000
      })
      verificationCode.value = '' // Limpar o código inválido
      return
    }

    // Salvar as configurações do 2FA
    console.log('Saving 2FA settings for user:', user.uid)
    const userRef = doc(db, 'users', user.uid)
    
    const twoFactorData = {
      twoFactorEnabled: true,
      twoFactorSecret: secret.value,
      twoFactorUpdatedAt: new Date().toISOString()
    }
    console.log('2FA data to save:', twoFactorData)
    
    await updateDoc(userRef, twoFactorData)
    console.log('2FA settings saved successfully')

    is2FAEnabled.value = true
    showSetupModal.value = false
    setupStep.value = 1 // Resetar para o primeiro passo
    verificationCode.value = '' // Limpar o código
    secret.value = '' // Limpar o segredo
    qrCodeUrl.value = '' // Limpar o QR code

    toast.add({
      severity: 'success',
      summary: 'Sucesso',
      detail: 'Autenticação em dois fatores ativada com sucesso!',
      life: 3000
    })
  } catch (error) {
    console.error('Error enabling 2FA:', error)
    toast.add({
      severity: 'error',
      summary: 'Erro',
      detail: error instanceof Error ? error.message : 'Não foi possível ativar o 2FA. Por favor, tente novamente.',
      life: 3000
    })
  } finally {
    loading.value = false
  }
}

async function disable2FA() {
  try {
    loading.value = true
    const user = authStore.user
    if (!user) throw new Error('Usuário não encontrado')

    const userRef = doc(db, 'users', user.uid)
    await updateDoc(userRef, {
      twoFactorEnabled: false,
      twoFactorSecret: null
    })

    is2FAEnabled.value = false
    toast.add({
      severity: 'success',
      summary: 'Sucesso',
      detail: 'Autenticação em dois fatores desativada com sucesso!',
      life: 3000
    })
  } catch (error) {
    console.error('Error disabling 2FA:', error)
    toast.add({
      severity: 'error',
      summary: 'Erro',
      detail: 'Não foi possível desativar o 2FA',
      life: 3000
    })
  } finally {
    loading.value = false
  }
}
</script>
